import React, { memo } from "react";
import { motion } from "framer-motion";
import { Badge } from "@/shared/components/ui/badge";
import { Progress } from "@/shared/components/ui/progress";
import {
  Activity,
  Zap,
  Clock,
  CheckCircle2,
  AlertTriangle,
  XCircle,
} from "lucide-react";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { getProviderName } from "@/shared/utils";

/* =============================
   🎨 Navy Design Tokens
============================= */
const C = {
  card: "#0E1C35",
  cardHigh: "#132443",
  border: "rgba(255,255,255,0.06)",
  textPrimary: "#E6EDF7",
  textMuted: "#8FA3BF",
  accentBlue: "#3B82F6",
  emerald: "#10B981",
  amber: "#F59E0B",
  red: "#EF4444",
};

/* =============================
   Status Config (Dark Styled)
============================= */
const statusConfig = {
  active: { icon: CheckCircle2, color: C.emerald },
  degraded: { icon: AlertTriangle, color: C.amber },
  inactive: { icon: XCircle, color: C.red },
  maintenance: { icon: Clock, color: C.textMuted },
};

function ProviderStatus({ providers = [], isLoading = false, onViewAll }) {
  const { t, language } = useLanguage();
  const visibleProviders = providers.slice(0, 2);

  const translateStatus = (status) => {
    if (!status) return t("na") || "N/A";
    const statusLower = String(status).toLowerCase().trim();
    const statusMap = {
      active: t("active"),
      inactive: t("inactive"),
      degraded: t("degraded"),
      maintenance: t("maintenance"),
    };
    return statusMap[statusLower] || status;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      whileHover={{ y: -2 }}
      style={{
        background: C.card,
        border: `1px solid ${C.border}`,
        borderRadius: 16,
      }}
      className="p-4 sm:p-5 shadow-lg h-full flex flex-col"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3
          className="text-lg font-semibold tracking-tight"
          style={{ color: C.textPrimary }}
        >
          {t("provider")} {t("status")}
        </h3>

        <Badge
          variant="outline"
          style={{
            borderColor: C.border,
            color: C.textMuted,
            background: C.cardHigh,
          }}
          className="gap-1 text-xs"
        >
          <Activity className="h-3 w-3" />
          {t("live")}
        </Badge>
      </div>

      <div className="space-y-4 flex-1">
        {isLoading ? (
          <div
            className="text-center py-8"
            style={{ color: C.textMuted }}
          >
            {t("loading") || "Loading..."}
          </div>
        ) : providers.length === 0 ? (
          <div
            className="text-center py-8"
            style={{ color: C.textMuted }}
          >
            {t("noProvidersConfigured")}
          </div>
        ) : (
          visibleProviders.map((provider, index) => {
            const statusKey = (provider.status || "").toLowerCase();
            const status = statusConfig[statusKey] || statusConfig.active;
            const StatusIcon = status.icon;

            return (
              <motion.div
                key={provider.id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.05 * index }}
                whileHover={{ y: -3 }}
                style={{
                  background: C.cardHigh,
                  border: `1px solid ${C.border}`,
                }}
                className="p-4 rounded-xl transition-all"
              >
                {/* Top Row */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                  <div className="flex items-center gap-2.5 min-w-0 flex-wrap">
                    <div
                      className="w-2 h-2 rounded-full shrink-0"
                      style={{
                        background: status.color,
                        boxShadow: `0 0 8px ${status.color}`,
                      }}
                    />
                    <span
                      className="font-medium"
                      style={{ color: C.textPrimary }}
                    >
                      {getProviderName(provider, language)}
                    </span>

                    <Badge
                      variant="outline"
                      className="text-xs shrink-0"
                      style={{
                        borderColor: C.border,
                        background: C.card,
                        color: C.textMuted,
                      }}
                    >
                      {provider.code}
                    </Badge>
                  </div>

                  <Badge
                    className="text-xs border-0 flex items-center gap-1 shrink-0 self-start sm:self-auto"
                    style={{
                      background: `${status.color}15`,
                      color: status.color,
                    }}
                  >
                    <StatusIcon className="h-3 w-3" />
                    {translateStatus(provider.status)}
                  </Badge>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <StatBlock
                    label={t("successRate")}
                    value={`${provider.success_rate || 100}%`}
                  >
                    <Progress
                      value={provider.success_rate || 100}
                      className="h-2"
                    />
                  </StatBlock>

                  <StatBlock
                    label={t("avgResponse")}
                    value={`${provider.avg_response_time || 0}ms`}
                    icon={<Zap className="h-4 w-4" style={{ color: C.amber }} />}
                  />

                  <StatBlock
                    label={t("healthScore")}
                    value={provider.health_score || 100}
                  >
                    <Progress
                      value={provider.health_score || 100}
                      className="h-2"
                    />
                  </StatBlock>
                </div>
              </motion.div>
            );
          })
        )}

        {!isLoading && providers.length > 2 && (
          <button
            type="button"
            onClick={onViewAll}
            className="w-full mt-2 py-2.5 rounded-xl text-sm font-semibold transition-all"
            style={{
              background: C.cardHigh,
              border: `1px solid ${C.border}`,
              color: C.textPrimary,
            }}
          >
            {t("viewAll") || "View All"}
          </button>
        )}
      </div>
    </motion.div>
  );
}

/* =============================
   Small Reusable Stat Block
============================= */

const StatBlock = memo(({ label, value, icon, children }) => (
  <div>
    <p
      className="mb-1 text-xs"
      style={{ color: C.textMuted }}
    >
      {label}
    </p>

    <div className="flex items-center gap-2">
      {icon}
      {children}
      <span
        className="font-medium"
        style={{ color: C.textPrimary }}
      >
        {value}
      </span>
    </div>
  </div>
));

export default memo(ProviderStatus);
